import {
  DocketFilterType,
  Sale,
} from "@towersystems/roam-common/lib/generated-types";
import SaleModel from "../data/model/Sale";
import DocketTemplate from "../data/model/DocketTemplate";
import {
  TemplateVariables,
  generateDocket,
} from "@towersystems/roam-common/lib/generate-docket";
import moment from "moment";
import { Database } from "@nozbe/watermelondb";
import { some } from "lodash";

export const generateHTML = async (
  sale: Sale | SaleModel,
  docketTemplate: DocketTemplate,
  database: Database
) => {
  const templateType = await docketTemplate.defaultTemplateType.fetch();

  const variables =
    sale instanceof SaleModel
      ? await saleModelToVariables(sale, docketTemplate, database)
      : await saleEntityToVariables(sale);

  const html = generateDocket(
    {
      ...variables,
      title: docketTemplate.name,
    },
    templateType.html,
    templateType.css
  );

  return html;
};

export const saleEntityToVariables = async (
  sale: Sale
): Promise<TemplateVariables> => {
  return {
    title: "Docket",
    saleNumber: sale.invoiceNumber,
    orderNumber: sale.purchaseOrderNumber ?? sale.invoiceNumber,
    saleDate: moment(sale.completedAt || new Date()).format("YYYY-MM-DD"),
    saleTime: moment(sale.completedAt || new Date()).format("HH:mm:ss"),
    staffName: sale?.staff?.initials,
    terminalName: sale?.terminal?.name,
    siteName: sale?.location?.name,
    lineItems: sale.lineItems.map((line) => ({
      quantity: line.quantity,
      productName: line.product?.name,
      customerNotes: line.customerNote,
    })),
    barcode: {
      code: sale.invoiceNumber,
      options: {
        text: ``,
        width: 3,
        height: 30,
        fontSize: 0,
        marginTop: 0,
        margin: 0,
      },
    },
  };
};

export const saleModelToVariables = async (
  sale: SaleModel,
  docketTemplate: DocketTemplate,
  database: Database
): Promise<TemplateVariables> => {
  const staff = await sale.staff.fetch();
  const terminal = await sale.terminal.fetch();
  const location = await sale.location.fetch();

  const lineItems: any[] = [];

  const allLineItems = await sale.items.fetch();

  if (docketTemplate.filters.length === 0) {
    lineItems.push(...allLineItems);
  } else {
    for (const item of allLineItems) {
      const product = await item.product.fetch();

      const result = some(docketTemplate.filters, (filter) => {
        if (filter.type === DocketFilterType.TAG) {
          // check if the tag array has any of the filter values
          if (
            filter.values.some((value) => (product?.tags ?? []).includes(value))
          ) {
            return true;
          }
        }

        if (filter.type === DocketFilterType.CATEGORY) {
          if (filter.values.some((value) => product?.category.id === value)) {
            return true;
          }
        }

        if (filter.type === DocketFilterType.DEPARTMENT) {
          if (filter.values.some((value) => product?.department.id === value)) {
            return true;
          }
        }
      });

      if (result) {
        lineItems.push(item);
      }
    }
  }

  if (lineItems.length === 0) {
    throw new Error("No line items match the filter criteria");
  }

  return {
    title: "Docket",
    saleNumber: sale.invoiceNumber,
    orderNumber: sale.purchaseOrderNumber ?? sale.invoiceNumber,
    saleDate: moment(sale.saleCompletedAt || new Date()).format("YYYY-MM-DD"),
    saleTime: moment(sale.saleCompletedAt || new Date()).format("HH:mm:ss"),
    staffName: staff?.initials,
    terminalName: terminal?.name,
    siteName: location?.name,
    lineItems: await Promise.all(
      lineItems.map(async (line) => {
        const product = await line.product.fetch();
        return {
          quantity: line.quantity,
          productName: product?.name,
          customerNotes: line.customerNote,
        };
      })
    ),
    barcode: {
      code: sale.invoiceNumber ?? "",
      options: {
        text: ``,
        width: 3,
        height: 30,
        fontSize: 0,
        marginTop: 0,
        margin: 0,
      },
    },
  };
};

export const applyDocketTemplateFilters = (
  sale: Sale | SaleModel,
  docketTemplate: DocketTemplate,
  database: Database
) => {};
